import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { QrCode, AlertTriangle, Wrench } from "lucide-react";
import AuthedImg from "../components/AuthedImg";

/** Asset detail — details + QR + linked history + Report Fault. */
export default function AssetDetailPage() {
    const { assetId } = useParams();
    const [data, setData] = useState(null);
    const [showFault, setShowFault] = useState(false);
    const [faultDesc, setFaultDesc] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        try { setData((await api.get(`/assets/${assetId}`)).data); }
        catch (e) { toast.error(apiError(e)); setData({ error: apiError(e) }); }
    }
    useEffect(() => { load(); }, [assetId]);

    async function reportFault() {
        setBusy(true);
        try {
            const r = await api.post(`/assets/${assetId}/report-fault`, { description: faultDesc });
            toast.success(`Fault raised. SR ID ${r.data.id.slice(0, 8)}`);
            setShowFault(false); setFaultDesc(""); load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    if (!data) return <div className="p-6 text-sm text-gray-500">Loading asset…</div>;
    if (data.error) return <div className="p-6 text-sm text-red-600">{data.error}</div>;
    const a = data.asset || {};

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6" data-testid="asset-detail-page">
            <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4">
                <div className="flex items-start gap-4">
                    <div className="flex-1">
                        <div className="text-xs uppercase tracking-wider text-gray-500">{a.type}</div>
                        <h2 className="text-xl font-semibold text-[#1E3355]" data-testid="asset-name">{a.name}</h2>
                        <div className="text-sm text-gray-700 mt-1">
                            {a.make} {a.model} · Serial: <b>{a.serial || "—"}</b>
                        </div>
                        <div className="text-sm text-gray-700">Location: {a.location} {a.floor}</div>
                        {a.source === "handover" && (
                            <div className="mt-2 inline-block text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid="asset-source-handover">
                                seeded from handover · review pending
                            </div>
                        )}
                    </div>
                    <div className="text-center">
                        <AuthedImg src={`/assets/${assetId}/qr.svg`} alt="QR" className="w-24 h-24 border border-gray-200" data-testid="asset-qr" />
                        <div className="text-[10px] text-gray-500 mt-1">Scan → this page</div>
                    </div>
                </div>
                {a.notes && <div className="mt-3 text-sm text-gray-600"><b>Notes:</b> {a.notes}</div>}
                <div className="mt-4 flex gap-2">
                    <button onClick={() => setShowFault(true)} className="inline-flex items-center gap-1 rounded-full bg-[#E02B2B] text-white text-sm px-3 py-1.5 hover:bg-red-700" data-testid="asset-report-fault-btn">
                        <Wrench className="w-4 h-4" /> Report fault
                    </button>
                </div>
            </div>

            {showFault && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="fault-form">
                    <label className="text-xs text-gray-600">Describe the fault</label>
                    <textarea value={faultDesc} onChange={(e) => setFaultDesc(e.target.value)} rows="3" className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="fault-description" />
                    <div className="flex gap-2 mt-2">
                        <button onClick={reportFault} disabled={busy || !faultDesc.trim()} className="rounded-full bg-[#1E3355] text-white text-sm px-3 py-1.5 disabled:opacity-50" data-testid="fault-submit">Raise SR</button>
                        <button onClick={() => setShowFault(false)} className="text-sm text-gray-600 px-3 py-1.5">Cancel</button>
                    </div>
                </div>
            )}

            {data.compliance && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="asset-compliance">
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">AMC / Compliance</div>
                    <div className="text-sm">
                        {data.compliance.type} · Last: {data.compliance.last_done_date || "—"} · Next due: {data.compliance.next_due_date || "—"}
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="asset-history">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Service history</div>
                {(data.service_requests || []).length === 0 ? (
                    <div className="text-xs text-gray-500">No service requests yet.</div>
                ) : (
                    <ul className="text-sm space-y-1">
                        {data.service_requests.map((s) => (
                            <li key={s.id} data-testid={`asset-sr-${s.id}`} className="border-b border-gray-100 py-1">
                                <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 mr-1 ${s.status === "Open" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{s.status}</span>
                                {s.description} <span className="text-xs text-gray-500 ml-1">— {s.created_at?.slice(0, 10)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
